import { enums as e, toast } from 'edrolo-ui';
import urlResolver from 'edrolo/utils/URLResolver';
import {
  Task,
  TaskLifecycleStage,
  TaskLifecycleStageAction,
  actionToTaskFieldMap,
} from '../types/Task';

const toastTitleByAction = {
  [TaskLifecycleStageAction.UNARCHIVE]: {
    icon: {
      name: e.Icons.SUCCESS,
      size: e.Size.LARGE,
      color: e.ColorHue.GREEN,
    },
    title: 'Task Restored',
    description:
      'The task has been restored and will now display on default filter settings.',
  },
  [TaskLifecycleStageAction.ARCHIVE]: {
    icon: {
      name: e.Icons.SUCCESS,
      size: e.Size.LARGE,
      color: e.ColorHue.GREEN,
    },
    title: 'Task Archived',
    description:
      'The task has been archived and will not display in your main task list for both you and your students.',
  },
  [TaskLifecycleStageAction.CLOSE]: {
    icon: {
      name: e.Icons.SUCCESS,
      size: e.Size.LARGE,
      color: e.ColorHue.GREEN,
    },
    title: 'Task Closed',
    description:
      'This task was successfully closed and is no longer accepting any student submissions.',
  },
};

export async function performTeacherTaskAction(
  action: TaskLifecycleStageAction,
  {
    task,
    csrfToken,
    mutate,
    mutateTasks,
    tasks,
  }: {
    task: Task;
    csrfToken: string;
    mutate: (
      data?: Task | Promise<Task>,
      opts?: { revalidate: boolean }
    ) => void;
    mutateTasks: (
      data?:
        | Task[]
        | Promise<Task[] | undefined>
        | ((tasks?: Task[]) => Task[] | undefined),
      opts?: { revalidate: boolean }
    ) => void;
    tasks?: Task[];
  }
): Promise<void> {
  const field = actionToTaskFieldMap[action];
  if (!field) {
    console.warn(`No task field mapping found for action: ${action}`);
    return;
  }

  const nowIso = new Date().toISOString();
  const valueByAction: Partial<
    Record<TaskLifecycleStageAction, string | null | TaskLifecycleStage>
  > = {
    [TaskLifecycleStageAction.UNARCHIVE]: null,
    [TaskLifecycleStageAction.ARCHIVE]: nowIso,
    [TaskLifecycleStageAction.CLOSE]: TaskLifecycleStage.CLOSED,
  };

  const newValue = valueByAction[action];
  const optimisticTask: Task = { ...task, [field]: newValue };

  const stageUrl = urlResolver.reverse('api:tasks-perform-task-action', {
    taskId: task.id,
  });

  mutate(optimisticTask, { revalidate: false });
  mutateTasks(
    (tasks?: Task[]) =>
      tasks?.map((t) => (t.id === task.id ? { ...t, [field]: newValue } : t)),
    { revalidate: false }
  );

  try {
    const response = await fetch(stageUrl, {
      method: 'POST',
      body: JSON.stringify({ action }),
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      },
    });

    if (!response.ok) {
      throw new Error('Action failed');
    }

    const updated: Task = await response.json();

    mutate(updated, { revalidate: false });
    mutateTasks(
      (tasks?: Task[]) =>
        tasks?.map((t) => (t.id === updated.id ? updated : t)),
      { revalidate: false }
    );
    toast(toastTitleByAction[action]);
  } catch (error) {
    console.error('Error performing task action:', error);
    mutate(task, { revalidate: false });
    mutateTasks(tasks, { revalidate: false });
    toast({
      icon: {
        name: e.Icons.CROSS,
        size: e.Size.LARGE,
        color: e.ColorHue.RED,
      },
      title: 'Action failed',
      description:
        'Please try again or contact support if the problem persists.',
    });
  }
}
